import { useEffect, useState, useMemo, useCallback } from "react";

import { toast } from "react-toastify";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getDemoUserPhones } from "../../services/demoUserService";

const toastCfg = { autoClose: 1500 };

/* ─── Sort icon ─── */
const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Format date nicely ─── */
const fmtDate = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d)) return raw;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  }) + "  " + d.toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export const DemouserLayer = () => {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);

  /* table state */
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting,      setSorting]      = useState([]);
  const [pagination,   setPagination]   = useState({ pageIndex: 0, pageSize: 10 });

  /* ── fetch ── */
  const fetchData = useCallback(async (silent = false) => {
    try {
      const res = await getDemoUserPhones();
      if (res?.status) setList(res.data || []);
    } catch (err) {
      if (!silent) toast.error("Failed to load demo users", toastCfg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(t);
  }, [fetchData]);

  /* ── stats ── */
  const stats = useMemo(() => {
    const today = new Date().toDateString();
    const todayCount = list.filter(
      (u) => u.created_at && new Date(u.created_at).toDateString() === today
    ).length;
    return { total: list.length, today: todayCount };
  }, [list]);

  /* ── columns ── */
  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 50,
      enableSorting: false,
      cell: ({ row }) =>
        row.index + 1 + pagination.pageIndex * pagination.pageSize,
    },
    {
      accessorKey: "demo_userid",
      header: "Demo User ID",
      cell: ({ getValue }) => (
        <span style={ts.demoId}>
          <Icon icon="solar:user-id-bold-duotone" style={{ fontSize: 13, marginRight: 5, verticalAlign: "middle" }} />
          {getValue() || "—"}
        </span>
      ),
    },
    {
      accessorKey: "phone",
      header: "Mobile",
      cell: ({ getValue }) => (
        <span style={ts.phone}>
          <Icon icon="solar:phone-calling-bold" style={{ fontSize: 12, marginRight: 5, color: "#94a3b8", verticalAlign: "middle" }} />
          {getValue() || "—"}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Registered At",
      cell: ({ getValue }) => (
        <span style={ts.date}>{fmtDate(getValue())}</span>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  /* ── table instance ── */
  const table = useReactTable({
    data: list,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel:       getCoreRowModel(),
    getFilteredRowModel:   getFilteredRowModel(),
    getSortedRowModel:     getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, value) => {
      const v = value.toLowerCase();
      return (
        (row.original.demo_userid || "").toLowerCase().includes(v) ||
        (row.original.phone       || "").includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const totalFiltered = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.root}>

      {/* ── Stats ── */}
      <div style={s.statsRow}>
        {[
          {
            label: "Total Demo Users", value: stats.total,
            icon: "solar:users-group-two-rounded-bold-duotone", color: "#4B5EAA",
          },
          {
            label: "Registered Today", value: stats.today,
            icon: "solar:calendar-add-bold-duotone", color: "#1da144",
          },
          {
            label: "Search Results", value: totalFiltered,
            icon: "solar:filter-bold-duotone", color: "#F0A500",
          },
        ].map((c) => (
          <div key={c.label} style={s.statCard}>
            <div style={{ ...s.statIcon, background: `${c.color}14`, color: c.color }}>
              <Icon icon={c.icon} style={{ fontSize: 20 }} />
            </div>
            <div>
              <div style={s.statValue}>{c.value}</div>
              <div style={s.statLabel}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Card ── */}
      <div style={s.card}>

        {/* Toolbar */}
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            {/* Search */}
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search by Demo ID or mobile…"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            {/* Page size */}
            <select
              style={s.select}
              value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>

          <div style={s.toolbarRight}>
            <button style={s.refreshBtn} onClick={() => fetchData()}>
              <Icon icon="solar:refresh-bold" style={{ fontSize: 14 }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading demo users…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:users-group-rounded-bold-duotone"
                      style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No demo users match your search
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fdf4")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc")}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>
              {totalFiltered === 0 ? 0 : pageIndex * pageSize + 1}–
              {Math.min((pageIndex + 1) * pageSize, totalFiltered)}
            </strong>{" "}
            of <strong>{totalFiltered}</strong> demo users
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}            disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}              disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const pg = Math.max(0, Math.min(pageIndex - 3, pageCount - 7)) + i;
              return (
                <button key={pg}
                  style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                  disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Styles ─── */
const s = {
  root: {
    padding: "20px 24px 40px",
    background: "#f1f5f9",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
  },

  /* stats */
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    marginBottom: 16,
  },
  statCard: {
    background: "#fff", borderRadius: 14,
    padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12,
    border: "1px solid #e8edf2",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  statIcon: {
    width: 40, height: 40, borderRadius: 10,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  statValue: { fontSize: "1.4rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  statLabel: {
    fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2,
  },

  /* card */
  card: {
    background: "#fff", borderRadius: 16,
    border: "1px solid #e8edf2",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  /* toolbar */
  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },

  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: {
    padding: "7px 30px 7px 32px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.82rem", color: "#334155", background: "#f8fafc",
    outline: "none", width: 260, fontFamily: "inherit",
  },
  clearBtn: {
    position: "absolute", right: 8, background: "none", border: "none",
    cursor: "pointer", color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  select: {
    padding: "7px 10px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "7px 14px",
    background: "#f8fafc", color: "#475569",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", fontWeight: 600, cursor: "pointer",
    fontFamily: "inherit",
  },

  /* table */
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    padding: "10px 14px",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: 700,
    fontSize: "0.71rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  td: {
    padding: "9px 14px",
    borderBottom: "1px solid #f8fafc",
    color: "#334155",
    verticalAlign: "middle",
  },
  emptyCell: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "0.85rem",
  },

  /* pagination */
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 8,
  },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: {
    minWidth: 30, height: 30,
    border: "1px solid #e2e8f0", background: "#fff",
    borderRadius: 7, cursor: "pointer",
    fontSize: "0.78rem", color: "#475569", fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  },
  pgBtnActive: {
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", fontWeight: 700,
  },
};

const ts = {
  demoId: {
    display: "inline-flex", alignItems: "center",
    background: "#f1f5f9", borderRadius: 6,
    padding: "3px 8px",
    fontSize: "0.78rem", fontWeight: 700,
    color: "#4B5EAA", fontFamily: "monospace",
  },
  phone: {
    fontSize: "0.82rem", color: "#334155",
    fontFamily: "monospace", display: "inline-flex", alignItems: "center",
  },
  date: {
    fontSize: "0.77rem", color: "#64748b", fontFamily: "monospace",
  },
};
